/**
 * AnchorTeaserCard — Dashboard widget for the AI Anchor Studio.
 *
 * Surfaces the freshest news story with a 30s script ready, and lets the
 * operator render it as a 9:16 talking-head MP4 (D-ID) without leaving
 * the dashboard. Uses the visual-style preset stored in localStorage
 * (`bf_news_visual_style_default`) so the chosen presenter travels with
 * the user from News Studio / News Center.
 */
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Video, Sparkles, Loader2, Play, Share2, ChevronRight,
  Newspaper, Mic, AlertCircle,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 3500;

export default function AnchorTeaserCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  const visualPick = (() => {
    try {
      const raw = localStorage.getItem("bf_news_visual_style_default");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const avatarId = visualPick?.avatar_id || "amy";

  const reload = async () => {
    try {
      const r = await api.get("/news/anchor-teaser");
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load anchor teaser");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);
  // Tick elapsed seconds while a render is in flight
  useEffect(() => {
    if (rendering) {
      const start = Date.now();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setElapsed(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [rendering]);

  const renderNow = async () => {
    if (!data?.story?.story_id) return;
    setRendering(true);
    setError("");
    toast.message(`Rendering anchor video · ~30s (presenter: ${avatarId})`);
    try {
      const body = {
        fmt: "vertical",
        theme: "amber_dark",
        visual_style: "avatar",
        avatar_id: avatarId,
        avatar_source_url: visualPick?.avatar_source_url || undefined,
        include_captions: true,
        duration_key: "radio_30",
      };
      const r = await api.post(`/news/stories/${data.story.story_id}/video`, body);
      toast.success("Anchor video ready");
      // Refresh teaser to pick up the new latest_video
      await reload();
      // Replace embedded video link
      setData((d) => d ? { ...d, latest_video: r.data } : d);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Render failed";
      setError(detail);
      toast.error(detail.slice(0, 120));
    } finally { setRendering(false); }
  };

  // -------------------------------------------------------------------------
  // Render states
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="anchor-teaser-loading">
        <div className="h-32 animate-pulse bg-bg rounded" />
      </div>
    );
  }

  // No story at all
  if (!data?.story) {
    return (
      <div
        className="rounded-xl border border-line bg-bg-surface p-5 flex items-center gap-4"
        data-testid="anchor-teaser-empty"
      >
        <div className="h-12 w-12 rounded-lg grid place-items-center" style={{ background: "#1a3a5c" }}>
          <Newspaper className="h-5 w-5" style={{ color: "#4a9eff" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink-primary">AI Anchor Studio</div>
          <div className="text-xs text-ink-muted mt-0.5">{data?.empty_reason || "No story yet — fetch an RSS feed or write one to get started."}</div>
        </div>
        <Link
          to="/dashboard/news"
          className="text-[11px] font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-md whitespace-nowrap"
          style={{ background: "#1a3a5c", color: "#4a9eff" }}
        >
          Open News Center <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    );
  }

  const v = data.latest_video;
  const hasVideo = !!v;

  return (
    <div
      className="rounded-xl border border-line bg-bg-surface overflow-hidden"
      data-testid="anchor-teaser-card"
      style={{ background: "linear-gradient(135deg, #0a0d14 0%, #0f1726 100%)" }}
    >
      <div className="p-5 flex items-start gap-5">
        {/* Left — preset avatar portrait or video preview */}
        <div className="shrink-0">
          {hasVideo ? (
            <video
              src={`${API_BASE}/news/videos/${v.video_id}/file`}
              className="w-32 h-56 object-cover rounded-lg"
              controls
              data-testid="anchor-teaser-video"
              poster=""
            />
          ) : (
            <div
              className="w-32 h-56 rounded-lg grid place-items-center overflow-hidden"
              style={{ background: "#0a0d14", border: "0.5px solid #1e2130" }}
              data-testid="anchor-teaser-placeholder"
            >
              <AnchorPlaceholder avatarId={avatarId} rendering={rendering} elapsed={elapsed} />
            </div>
          )}
        </div>

        {/* Right — story metadata + actions */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider"
              style={{ background: "#1a3a5c", color: "#4a9eff" }}
              data-testid="anchor-teaser-badge"
            >
              <Mic className="h-2.5 w-2.5" /> AI Anchor Studio
            </div>
            {!data.story.has_script && (
              <div className="inline-flex items-center gap-1 text-[10px]" style={{ color: "#f59e0b" }}>
                <AlertCircle className="h-3 w-3" />
                Script pending
              </div>
            )}
          </div>

          <div
            className="text-base font-semibold leading-tight text-ink-primary line-clamp-2"
            data-testid="anchor-teaser-headline"
          >
            {data.story.headline || "Today's top story"}
          </div>
          {data.story.summary && (
            <div className="text-[11px] text-ink-muted leading-relaxed line-clamp-2">
              {data.story.summary}
            </div>
          )}

          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {!hasVideo ? (
              <button
                type="button"
                onClick={renderNow}
                disabled={rendering || !data.ready_to_render}
                data-testid="anchor-teaser-render"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-semibold transition disabled:opacity-50"
                style={{ background: "#1a3a5c", color: "#4a9eff" }}
              >
                {rendering
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering · {elapsed}s</>
                  : <><Sparkles className="h-3.5 w-3.5" /> Render as anchor video</>
                }
              </button>
            ) : (
              <>
                <Link
                  to={`/dashboard/news?story=${data.story.story_id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-semibold transition"
                  style={{ background: "#1a3a5c", color: "#4a9eff" }}
                  data-testid="anchor-teaser-open"
                >
                  <Play className="h-3.5 w-3.5" /> Open in News Center
                </Link>
                <button
                  type="button"
                  onClick={renderNow}
                  disabled={rendering}
                  data-testid="anchor-teaser-rerender"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium border transition"
                  style={{ background: "#0f1117", color: "#c8ccd8", border: "0.5px solid #1e2130" }}
                >
                  {rendering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Re-render
                </button>
              </>
            )}
            <Link
              to="/dashboard/news"
              className="text-[11px] text-ink-muted hover:text-ink-primary inline-flex items-center gap-0.5 ml-auto"
              data-testid="anchor-teaser-all-news"
            >
              All news <ChevronRight className="h-3 w-3" />
            </Link>
          </div>

          {error && (
            <div className="text-[11px] mt-1" style={{ color: "#ef4444" }} data-testid="anchor-teaser-error">
              {error.slice(0, 220)}
            </div>
          )}

          {hasVideo && (
            <div className="mt-1 flex items-center gap-2 text-[10px] font-mono" style={{ color: "#4a5068" }}>
              <Video className="h-3 w-3" />
              <span>{v.width}×{v.height} · {v.visual_style}</span>
              {v.visual_meta?.avatar_id && <span>· {v.visual_meta.avatar_id}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function AnchorPlaceholder({ avatarId, rendering, elapsed }) {
  // Animated equalizer bars while rendering, otherwise a mic icon
  if (rendering) {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-end gap-0.5 h-10">
          {Array.from({ length: 8 }).map((_, i) => (
            <span
              key={i}
              className="w-1 rounded-t animate-pulse"
              style={{
                height: `${30 + Math.sin((elapsed + i) / 1.3) * 40 + 10}%`,
                background: "#4a9eff",
                animationDelay: `${i * 80}ms`,
              }}
            />
          ))}
        </div>
        <div className="text-[10px] font-mono" style={{ color: "#4a9eff" }}>
          presenter · {avatarId}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="h-12 w-12 rounded-full grid place-items-center"
        style={{ background: "#1a3a5c", border: "0.5px solid #4a9eff66" }}
      >
        <Sparkles className="h-5 w-5" style={{ color: "#4a9eff" }} />
      </div>
      <div className="text-[10px] font-mono" style={{ color: "#4a5068" }}>
        ready · {avatarId}
      </div>
      <Share2 className="h-3 w-3 opacity-30" style={{ color: "#4a5068" }} />
    </div>
  );
}
